import React, { JSX } from 'react';
//import styles from './styles/Editor.module.scss';
import styles from './styles/ImageDialog.module.scss';

interface IFileEntry {
  name: string;
  path: string;
  isDir: boolean;
  url: string;
  size: number | null;
  isEmpty?: boolean;
}

interface IImageDialogProps {
  apiBase: string;
  onSelect: (url: string) => void;
  onCancel: () => void;
  folderSelectMode?: boolean;
}

interface IImageDialogState {
  currentPath: string;
  entries: IFileEntry[];
  selectedFile: IFileEntry | null;
  isLoading: boolean;
  error: string;
  isDragging: boolean;
  deleteConfirmFile: IFileEntry | null;
  uploadStatus: string;
  recentlyUploadedNames: string[];
}

export default class ImageDialog extends React.Component<IImageDialogProps, IImageDialogState> {
  private fileInputRef = React.createRef<HTMLInputElement>();
  private fileGridRef = React.createRef<HTMLDivElement>();

  constructor(props: IImageDialogProps) {
    super(props);
    this.state = {
      currentPath: '',
      entries: [],
      selectedFile: null,
      isLoading: false,
      error: '',
      isDragging: false,
      deleteConfirmFile: null,
      uploadStatus: '',
      recentlyUploadedNames: [],
    };
  }

  componentDidMount(): void {
    this.browse('');
  }

  // ─── Image Conversion ────────────────────────────────────────────────────────

  private convertToWebPIfBeneficial = (file: File): Promise<{ file: File; wasConverted: boolean }> => {
    return new Promise((resolve) => {
      // Wenn bereits WebP, nicht konvertieren
      if (file.type === 'image/webp') {
        resolve({ file, wasConverted: false });
        return;
      }

      // Nur für Image-Typen konvertieren
      if (!file.type.startsWith('image/')) {
        resolve({ file, wasConverted: false });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            resolve({ file, wasConverted: false });
            return;
          }

          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve({ file, wasConverted: false });
                return;
              }

              // Vergleich: Ist WebP kleiner?
              if (blob.size < file.size) {
                const newName = file.name.replace(/\.[^.]+$/, '.webp');
                const webpFile = new File([blob], newName, { type: 'image/webp' });
                console.log(`[WebP Conversion] ${file.name} (${(file.size / 1024).toFixed(1)}KB) → ${newName} (${(blob.size / 1024).toFixed(1)}KB)`);
                resolve({ file: webpFile, wasConverted: true });
              } else {
                console.log(`[WebP Conversion] ${file.name} bleibt im Original-Format (WebP wäre größer)`);
                resolve({ file, wasConverted: false });
              }
            },
            'image/webp',
            0.85 // 85% Qualität für gutes Verhältnis zwischen Größe und Qualität
          );
        };

        img.onerror = () => {
          console.warn(`[WebP Conversion] Fehler beim Laden von ${file.name}`);
          resolve({ file, wasConverted: false });
        };

        img.src = e.target?.result as string;
      };

      reader.onerror = () => {
        console.warn(`[WebP Conversion] FileReader-Fehler für ${file.name}`);
        resolve({ file, wasConverted: false });
      };

      reader.readAsDataURL(file);
    });
  };

  // ─── API ─────────────────────────────────────────────────────────────────────

  private browse = (path: string): void => {
    this.setState({ isLoading: true, error: '', selectedFile: null });
    fetch(`${this.props.apiBase}?action=browse&path=${encodeURIComponent(path)}`)
      .then(async (r) => {
        const raw = await r.text();
        let data: { success?: boolean; error?: string; path?: string; items?: IFileEntry[] } = {};
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch {
          throw new Error('Ungültige Serverantwort beim Laden des Ordners');
        }

        if (!r.ok || !data.success) {
          throw new Error(data.error ?? `Ordner konnte nicht geladen werden (HTTP ${r.status})`);
        }

        return data;
      })
      .then((data) => {
        this.setState({ currentPath: data.path ?? '', entries: data.items ?? [], isLoading: false });
      })
      .catch((e: unknown) => {
        const message = e instanceof Error ? e.message : 'Netzwerkfehler';
        console.log('[ImageDialog browse] Fehler:', e);
        this.setState({ error: message, isLoading: false });
      });
  };

  private openFolder = (entry: IFileEntry): void => {
    if (this.state.isLoading) {
      return;
    }

    const basePath = this.state.currentPath.replace(/\/+$/g, '');
    const fallbackPath = (basePath ? `${basePath}/${entry.name}` : entry.name).replace(/^\/+|\/+$/g, '');
    const targetPath = (entry.path || fallbackPath).replace(/^\/+|\/+$/g, '');
    this.browse(targetPath);
  };

  private createFolder = (): void => {
    const { currentPath, isLoading } = this.state;
    if (isLoading) {
      return;
    }

    const folderName = window.prompt('Name des neuen Ordners:', '');
    if (folderName === null) {
      return;
    }

    this.setState({ isLoading: true, error: '', uploadStatus: 'Ordner wird angelegt…' });
    fetch(`${this.props.apiBase}?action=create-folder&path=${encodeURIComponent(currentPath)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: folderName.trim() }),
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok || !data.success) {
          throw new Error(data.error ?? 'Ordner konnte nicht angelegt werden');
        }
        return data;
      })
      .then(() => {
        this.setState({ uploadStatus: 'Ordner angelegt', selectedFile: null });
        this.browse(currentPath);
        window.setTimeout(() => this.setState({ uploadStatus: '' }), 2500);
      })
      .catch((e: unknown) => {
        const message = e instanceof Error ? e.message : 'Ordner konnte nicht angelegt werden';
        this.setState({ error: message, uploadStatus: '', isLoading: false });
      });
  };

  private renameSelectedFolder = (): void => {
    const { selectedFile, currentPath, isLoading } = this.state;
    if (isLoading || !selectedFile || !selectedFile.isDir) {
      return;
    }

    const newName = window.prompt('Neuer Ordnername:', selectedFile.name);
    if (newName === null) {
      return;
    }

    this.setState({ isLoading: true, error: '', uploadStatus: 'Ordner wird umbenannt…' });
    fetch(`${this.props.apiBase}?action=rename`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: selectedFile.path, name: newName.trim() }),
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok || !data.success) {
          throw new Error(data.error ?? 'Ordner konnte nicht umbenannt werden');
        }
        return data;
      })
      .then(() => {
        this.setState({ uploadStatus: 'Ordner umbenannt', selectedFile: null });
        this.browse(currentPath);
        window.setTimeout(() => this.setState({ uploadStatus: '' }), 2500);
      })
      .catch((e: unknown) => {
        const message = e instanceof Error ? e.message : 'Ordner konnte nicht umbenannt werden';
        this.setState({ error: message, uploadStatus: '', isLoading: false });
      });
  };

  private openSelectedFolder = (): void => {
    const { selectedFile } = this.state;
    if (selectedFile?.isDir) {
      this.openFolder(selectedFile);
    }
  };

  private handleGridKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const { entries, selectedFile, isLoading } = this.state;
    const { folderSelectMode, onSelect } = this.props;
    if (isLoading) return;

    const visibleEntries = entries.filter((entry) => folderSelectMode ? entry.isDir : true);

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const idx = visibleEntries.findIndex((entry) => entry.path === selectedFile?.path);
      const next = visibleEntries[idx + 1] ?? visibleEntries[0];
      if (next) this.selectAndScroll(next);
      return;
    }

    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const idx = visibleEntries.findIndex((entry) => entry.path === selectedFile?.path);
      const prev = idx > 0 ? visibleEntries[idx - 1] : visibleEntries[visibleEntries.length - 1];
      if (prev) this.selectAndScroll(prev);
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (!selectedFile) return;
      if (selectedFile.isDir) {
        this.openFolder(selectedFile);
      } else if (!folderSelectMode) {
        onSelect(selectedFile.url);
      }
      return;
    }

    // Buchstaben-/Ziffern-Schnellnavigation
    if (e.key.length !== 1 || !/[a-zA-Z0-9]/.test(e.key)) return;
    e.preventDefault();

    const char = e.key.toLowerCase();
    const matching = visibleEntries.filter((entry) => entry.name.toLowerCase().startsWith(char));
    if (matching.length === 0) return;

    let nextEntry = matching[0];
    if (selectedFile) {
      const currentIdx = matching.findIndex((entry) => entry.path === selectedFile.path);
      if (currentIdx !== -1) {
        nextEntry = matching[(currentIdx + 1) % matching.length];
      }
    }

    this.selectAndScroll(nextEntry);
  };

  private selectAndScroll = (entry: IFileEntry): void => {
    this.setState({ selectedFile: entry }, () => {
      const el = this.fileGridRef.current?.querySelector<HTMLElement>(`[data-entry-path="${CSS.escape(entry.path)}"]`);
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  };

  private uploadFiles = (files: FileList): void => {
    if (this.state.isLoading) {
      return;
    }

    const { currentPath } = this.state;
    this.setState({ uploadStatus: 'Wird vorbereitet…', isLoading: true });
    let uploadedNames: string[] = [];

    // Konvertiere alle Bilder zu WebP falls vorteilhaft
    (async () => {
      try {
        const processedFiles: File[] = [];
        const fileArray = Array.from(files);

        for (let i = 0; i < fileArray.length; i++) {
          const file = fileArray[i];
          this.setState({ uploadStatus: `Verarbeite ${i + 1}/${fileArray.length}…` });
          const result = await this.convertToWebPIfBeneficial(file);
          processedFiles.push(result.file);
        }

        // Upload verarbeitete Dateien
        uploadedNames = processedFiles.map((f) => f.name);
        this.setState({ uploadStatus: 'Wird hochgeladen…' });
        const formData = new FormData();
        processedFiles.forEach((f) => formData.append('files[]', f));

        return fetch(`${this.props.apiBase}?action=upload&path=${encodeURIComponent(currentPath)}`, {
          method: 'POST',
          body: formData,
        });
      } catch (error) {
        console.error('[Upload] Fehler bei Verarbeitung:', error);
        this.setState({ uploadStatus: 'Verarbeitungsfehler', isLoading: false });
        return Promise.reject(error);
      }
    })()
      .then((r) => r.json())
      .then((data) => {
        const msg = data.success
          ? `${data.uploaded?.length ?? 0} Datei(en) hochgeladen`
          : (data.errors?.[0] ?? 'Upload fehlgeschlagen');
        if (data.success) {
          this.setState({ uploadStatus: msg, recentlyUploadedNames: uploadedNames });
          window.setTimeout(() => this.setState({ recentlyUploadedNames: [] }), 2500);
        } else {
          this.setState({ uploadStatus: msg });
        }
        this.browse(currentPath);
        window.setTimeout(() => this.setState({ uploadStatus: '' }), 2500);
      })
      .catch(() => this.setState({ uploadStatus: 'Upload-Fehler', isLoading: false }));
  };

  private confirmDelete = (): void => {
    const { deleteConfirmFile, currentPath, isLoading } = this.state;
    if (isLoading) return;
    if (!deleteConfirmFile) return;

    this.setState({ isLoading: true });
    fetch(`${this.props.apiBase}?action=delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: deleteConfirmFile.path }),
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok || !data.success) {
          throw new Error(data.error ?? 'Löschen fehlgeschlagen');
        }
        return data;
      })
      .then(() => {
        this.setState({ deleteConfirmFile: null, selectedFile: null, uploadStatus: 'Eintrag gelöscht' });
        this.browse(currentPath);
        window.setTimeout(() => this.setState({ uploadStatus: '' }), 2500);
      })
      .catch((e: unknown) => {
        const message = e instanceof Error ? e.message : 'Löschen fehlgeschlagen';
        this.setState({ deleteConfirmFile: null, error: message, isLoading: false });
      });
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  private getBreadcrumbs(): Array<{ label: string; path: string }> {
    const parts = this.state.currentPath.split('/').filter(Boolean);
    const crumbs: Array<{ label: string; path: string }> = [{ label: 'Root', path: '' }];
    parts.forEach((part, i) => {
      crumbs.push({ label: part, path: parts.slice(0, i + 1).join('/') });
    });
    return crumbs;
  }

  private isImageFile(name: string): boolean {
    return /\.(jpe?g|png|gif|webp|svg|bmp)$/i.test(name);
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  public render(): React.ReactNode {
    const { onSelect, onCancel, folderSelectMode } = this.props;
    const { entries, selectedFile, isLoading, error, isDragging, deleteConfirmFile, uploadStatus } = this.state;
    const crumbs = this.getBreadcrumbs();

    const dialogButtons: JSX.Element = (
      <div className={styles.dialogButtons}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {folderSelectMode ? (
            // Ordner-Auswahl-Modus: aktuellen oder ausgewählten Ordner übernehmen
            <>
              {selectedFile?.isDir && (
                <button
                  type="button"
                  className="mw-button mw-button--ghost mw-button--small"
                  disabled={isLoading}
                  onClick={this.openSelectedFolder}
                >
                  <div className="mw-button-outer"><div className="mw-button-inner">Öffnen</div></div>
                </button>
              )}
              <button
                type="button"
                className="mw-button mw-button--ghost mw-button--small"
                disabled={isLoading}
                onClick={() => onSelect(this.state.currentPath)}
              >
                <div className="mw-button-outer"><div className="mw-button-inner">Diesen Ordner wählen</div></div>
              </button>
            </>
          ) : (
            // Normal-Modus: Bild oder Ordner-Aktionen
            <>
              {selectedFile && !selectedFile.isDir && (
                <button
                  type="button"
                  className="mw-button mw-button--ghost mw-button--small mw-button--red mw-button--alert"
                  disabled={isLoading}
                  onClick={() => onSelect(selectedFile.url)}
                >
                  <div className="mw-button-outer"><div className="mw-button-inner">Auswählen</div></div>
                </button>
              )}
              {selectedFile?.isDir && (
                <>
              <button
                type="button"
                className="mw-button mw-button--ghost mw-button--small"
                disabled={isLoading}
                onClick={this.openSelectedFolder}
              >
                <div className="mw-button-outer"><div className="mw-button-inner">Öffnen</div></div>
              </button>
              <button
                type="button"
                className="mw-button mw-button--ghost mw-button--small"
                disabled={isLoading}
                onClick={this.renameSelectedFolder}
              >
                <div className="mw-button-outer"><div className="mw-button-inner">Umbenennen</div></div>
              </button>
            </>
          )}
            </> // end Normal-Modus
          )}
        </div>
        <div className={styles.dialogButtonsRight}>
          {!folderSelectMode && (
            <button
              type="button"
              className="mw-button mw-button--ghost mw-button--small"
              disabled={isLoading}
              onClick={this.createFolder}
            >
              <div className="mw-button-outer"><div className="mw-button-inner">Ordner anlegen</div></div>
            </button>
          )}
          {!folderSelectMode && selectedFile && (!selectedFile.isDir || selectedFile.isEmpty) && (
            <button
              type="button"
              className="mw-button mw-button--ghost mw-button--red mw-button--small"
              disabled={isLoading}
              onClick={() => this.setState({ deleteConfirmFile: selectedFile })}
            >
              <div className="mw-button-outer"><div className="mw-button-inner">Löschen</div></div>
            </button>
          )}
          <button type="button" className="mw-button mw-button--ghost mw-button--small" disabled={isLoading} onClick={onCancel}>
            <div className="mw-button-outer"><div className="mw-button-inner">Abbrechen</div></div>
          </button>
        </div>
      </div>
    );

    return (
      <div className={styles.dialogOverlay} onClick={(e) => { if (!isLoading && e.target === e.currentTarget) onCancel(); }}>
        <div className={styles.dialog}>

          {/* Header */}
          <div className={styles.dialogHeader}>
            <span>{folderSelectMode ? 'Ordner auswählen' : 'Bild auswählen'}</span>
            <button type="button" className={styles.dialogClose} disabled={isLoading} onClick={onCancel}>✕</button>
          </div>

          {/* Breadcrumbs */}
          <div className={styles.breadcrumbs}>
            {crumbs.map((c, i) => (
              <span key={c.path}>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a href="#" onClick={(e) => { e.preventDefault(); if (!isLoading) { this.browse(c.path); } }}>{c.label}</a>
                {i < crumbs.length - 1 && <span style={{ margin: '0 4px' }}>›</span>}
              </span>
            ))}
          </div>

          {/* Drop zone - nur im normalen Modus */}
          {!folderSelectMode && (
            <>
              <div
                className={`${styles.dropZone}${isDragging ? ` ${styles.dropZoneActive}` : ''}`}
                onDragOver={(e) => { e.preventDefault(); if (!isLoading) { this.setState({ isDragging: true }); } }}
                onDragLeave={() => this.setState({ isDragging: false })}
                onDrop={(e) => {
                  e.preventDefault();
                  this.setState({ isDragging: false });
                  if (!isLoading && e.dataTransfer.files.length > 0) this.uploadFiles(e.dataTransfer.files);
                }}
                onClick={() => { if (!isLoading) { this.fileInputRef.current?.click(); } }}
                style={isLoading ? { pointerEvents: 'none', opacity: 0.65 } : undefined}
              >
                {uploadStatus || 'Dateien hierher ziehen oder klicken zum Hochladen'}
              </div>
              <input
                ref={this.fileInputRef}
                type="file"
                multiple
                disabled={isLoading}
                style={{ display: 'none' }}
                onChange={(e) => { if (!isLoading && e.target.files) this.uploadFiles(e.target.files); }}
              />
            </>
          )}

          {/* Status */}
          {isLoading && <div className={styles.dialogStatus}>Lade…</div>}
          {error && <div className={styles.dialogError}>{error}</div>}

          {/* File grid */}
          <div
            ref={this.fileGridRef}
            className={styles.fileGrid}
            tabIndex={0}
            onKeyDown={this.handleGridKeyDown}
            style={{ outline: 'none' }}
          >
            {entries.filter((entry) => folderSelectMode ? entry.isDir : true).map((entry) => {
              const isSelected = selectedFile?.path === entry.path;
              const isUploaded = this.state.recentlyUploadedNames.includes(entry.name);
              let entryClass = styles.fileEntry;
              if (isSelected) entryClass += ` ${styles.fileEntrySelected}`;
              else if (isUploaded) entryClass += ` ${styles.fileEntryUploaded}`;
              return (
              <div
                key={entry.path}
                className={entryClass}
                data-entry-path={entry.path}
                tabIndex={-1}
                onClick={() => {
                  if (isLoading) {
                    return;
                  }
                  this.setState({ selectedFile: entry });
                  this.fileGridRef.current?.focus();
                }}
                onDoubleClick={() => {
                  if (isLoading) {
                    return;
                  }
                  if (entry.isDir) {
                    this.openFolder(entry);
                  } else {
                    onSelect(entry.url);
                  }
                }}
                title={entry.name}
                style={isLoading ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
              >
                {entry.isDir ? (
                  <div className={styles.folderItem}>
                    <span className={styles.folderIcon}>📁</span>
                    <span className={styles.entryName}>{entry.name}</span>
                  </div>
                ) : (
                  <div className={styles.fileItem}>
                    {this.isImageFile(entry.name) ? (
                      <img src={entry.url} alt={entry.name} className={styles.filePreviewImg} />
                    ) : (
                      <span className={styles.fileIcon}>📄</span>
                    )}
                    <span className={styles.entryName}>{entry.name}</span>
                  </div>
                )}
              </div>
          );
          })}
          </div>

          {/* Dialog action buttons */}
          {dialogButtons}
        </div>

        {/* Delete confirm dialog */}
        {deleteConfirmFile && (
          <div className={styles.dialogOverlay} style={{ zIndex: 10001 }} onClick={(e) => { if (!isLoading && e.target === e.currentTarget) this.setState({ deleteConfirmFile: null }); }}>
            <div className={styles.deletedialog} style={{ maxWidth: 400 }}>
              <div className={styles.dialogHeader}>
                <span>Bestätigung</span>
                <button type="button" className={styles.dialogClose} disabled={isLoading} onClick={() => this.setState({ deleteConfirmFile: null })}>✕</button>
              </div>
              <div style={{ padding: '12px 16px' }}>
                <p style={{ margin: 0, fontSize: '16px' }}>
                  Soll {deleteConfirmFile.isDir ? `der Ordner ` : `die Datei `}
                  <strong>{deleteConfirmFile.name}</strong>&nbsp;wirklich gelöscht werden?
                </p>                
              </div>
              <div className={styles.dialogButtons}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button 
                    type="button" 
                    className="mw-button mw-button--ghost mw-button--red mw-button--small" 
                    disabled={isLoading} 
                    onClick={this.confirmDelete}
                  >
                    <div className="mw-button-outer"><div className="mw-button-inner">Löschen</div></div>
                  </button>
                  <button 
                    type="button" 
                    className="mw-button mw-button--ghost mw-button--small" 
                    disabled={isLoading} 
                    onClick={() => this.setState({ deleteConfirmFile: null })}
                  >
                    <div className="mw-button-outer"><div className="mw-button-inner">Abbrechen</div></div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}
